/**
 * AppLauncher.jsx — Apps menu. Coming Soon tiles stay visible and do not navigate.
 * Shaped by: Prompt 21; launcher prompt L1; D-42-05, D-42-06 (owner 2026-10-12).
 * Invariants: coming soon is not a blocked route; one ChartMind tile; tiles are not primary buttons.
 *   The menu lists only apps this account can open. There is no Locked tile.
 * Related: appCatalog.js, useLauncherAuthState.js, RetiredAppPage.jsx.
 */
import React, { useMemo, useState } from "react";
import {
  Box,
  ButtonBase,
  IconButton,
  Paper,
  Popover,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AppsIcon from "@mui/icons-material/Apps";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "hooks/useAuth";
import { APP_TOP_BAR_ACTION_HIT_PX, APP_TOP_BAR_ACTION_ICON_PX } from "layout/appTopBarLayout";
import { getVisibleLauncherApps } from "./appCatalog";
import useLauncherAuthState from "./useLauncherAuthState";

const AppTile = ({ app, onOpen }) => {
  const Icon = app.icon;
  const comingSoon = app.comingSoon === true;
  const disabled = !app.to && !app.href;
  const openable = !disabled && !comingSoon;

  const card = (
    <Paper
      variant="outlined"
      sx={{
        position: "relative",
        overflow: "hidden",
        width: "100%",
        height: "100%",
        minHeight: 44,
        p: 1.5,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 0.75,
        borderRadius: 2,
        opacity: openable ? 1 : 0.72,
        transition: (theme) =>
          theme.transitions.create(["background-color", "border-color"]),
        ...(openable && {
          "&:hover": { bgcolor: "action.hover" },
        }),
      }}
    >
      {comingSoon && (
        <Typography
          variant="caption"
          component="span"
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            px: 0.75,
            color: "text.secondary",
            bgcolor: "action.hover",
            borderRadius: 1,
          }}
        >
          Coming soon
        </Typography>
      )}
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: openable ? "primary.main" : "text.disabled",
          bgcolor: (theme) =>
            openable
              ? alpha(theme.palette.primary.main, 0.1)
              : theme.palette.action.hover,
        }}
      >
        <Icon fontSize="medium" />
      </Box>
      <Typography
        variant="body2"
        fontWeight={600}
        color={openable ? "text.primary" : "text.secondary"}
        noWrap
      >
        {app.name}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ lineHeight: 1.2 }}
      >
        {app.description}
      </Typography>
    </Paper>
  );

  return (
    <ButtonBase
      data-testid={`launcher.apps.${app.id}`}
      aria-disabled={comingSoon ? "true" : undefined}
      aria-label={comingSoon ? `${app.name}, coming soon` : app.name}
      onClick={(event) => {
        if (!openable) {
          event.preventDefault();
          return;
        }
        onOpen(app);
      }}
      onKeyDown={(event) => {
        if (!comingSoon) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          event.stopPropagation();
        }
      }}
      sx={{
        borderRadius: 2,
        textAlign: "center",
        alignItems: "stretch",
        minHeight: 44,
        cursor: openable ? "pointer" : "default",
      }}
    >
      {card}
    </ButtonBase>
  );
};

const AppLauncher = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const { authState, loading } = useLauncherAuthState();
  const [anchorEl, setAnchorEl] = useState(null);

  const apps = useMemo(
    () => getVisibleLauncherApps(authState, pathname),
    [authState, pathname],
  );

  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleNavigate = (appOrTo) => {
    handleClose();
    if (appOrTo && typeof appOrTo === "object") {
      if (appOrTo.comingSoon) return;
      if (appOrTo.href) {
        window.open(appOrTo.href, "_blank", "noopener,noreferrer");
        return;
      }
      if (appOrTo.to) {
        navigate(appOrTo.to);
      }
      return;
    }
    navigate(appOrTo);
  };

  if (loading || !user?.uid || apps.length === 0) return null;

  const open = Boolean(anchorEl);

  return (
    <>
      <IconButton
        onClick={handleOpen}
        aria-label="Apps"
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        sx={{
          width: APP_TOP_BAR_ACTION_HIT_PX,
          height: APP_TOP_BAR_ACTION_HIT_PX,
          flexShrink: 0,
          color: "primary.main",
        }}
      >
        <AppsIcon sx={{ fontSize: APP_TOP_BAR_ACTION_ICON_PX }} />
      </IconButton>
      <Popover
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            width: 384,
            maxWidth: "calc(100vw - 32px)",
            maxHeight: "calc(100vh - 32px)",
            p: 2,
            boxSizing: "border-box",
            overflowY: "auto",
          },
        }}
      >
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
          Your apps
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gap: 1,
          }}
        >
          {apps.map((app) => (
            <AppTile key={app.id} app={app} onOpen={handleNavigate} />
          ))}
        </Box>
      </Popover>
    </>
  );
};

export default AppLauncher;
